import React, { useEffect, useMemo, useRef, useState } from 'react';
import HookForm from '../../components/HookForm/HookForm';
import Select from '../../components/Select/Select';
import SelectMarks from '../../components/Selects/SelectMarks';
import SelectModels from '../../components/Selects/SelectModels';
import SelectComplectations from '../../components/Selects/SelectComplectations';
import SelectColor, { COLORS } from '../../components/Selects/SelectColor';
import SelectCity from '../../components/Selects/SelectCity';
import SelectWithFetch from '../../components/Selects/SelectWithFetch';
import getTransmissions from '../../api/shop/getTransmissions';
import getContainers from '../../api/cars/getContainers';
import getDcList from '../../api/cars/getDcList';
import AddModelModal from './AddModelModal';
import { decomposeOrigin } from './useOriginOptions';
import styles from './stockPendingRowsTable.module.css';

const ENGINE_TYPE_OPTIONS = ['Бензин', 'Дизель', 'Электро', 'Гибрид'].map((label) => ({
  value: label,
  label,
}));
// Коробки берём из справочника, а не из константы: список ведут менеджеры в карточке
// машины, и захардкоженный набор с ним разъезжался (на проде так появилась позиция
// «8 АТ», которую сток-загрузка не принимала). Значение — title, а не id: бэк матчит
// КПП по названию (FindTransmissionId), id ему не нужен
const transmissionOption = (item) => ({ value: item.title, label: item.title });
const RowTransmissionSelect = (props) => (
  <SelectWithFetch fetch={getTransmissions} formatOption={transmissionOption} {...props} />
);

const findByName = (list, name, nameKey) => {
  if (!name) return null;
  return list.find((item) => (item[nameKey] || '').toLowerCase() === name.toLowerCase()) || null;
};

// Единственный ДЦ на строку — components/Selects/SelectDc.jsx в проекте всегда
// мультиселект (машина может числиться за несколькими ДЦ), а тут нужен ровно один.
// Список ДЦ грузится ЛЕНИВО по фокусу (как SelectMarks/SelectModels/SelectCity),
// а не сразу при резолве города/марки — иначе на десятках строк с одинаковым
// заранее подставленным городом+маркой одновременно летит куча одинаковых запросов.
// dcCache — общий на всю таблицу кэш (город+марка обычно повторяются в разных
// строках одного файла), чтобы не тянуть один и тот же список по новой
const RowDcSelect = ({ value, onChange, dependantValue, label, placeholder, error, required, dcCache }) => {
  const cityOption = dependantValue?.[0];
  const markOption = dependantValue?.[1];
  const cityId = cityOption?.value;
  const mark = markOption?.label;
  const depKey = `${cityId || ''}|${mark || ''}`;

  const [options, setOptions] = React.useState([]);
  const [isLoading, setIsLoading] = React.useState(false);
  const isFirstRun = useRef(true);

  // Сброс при смене города/марки — без сети, сама загрузка идёт по фокусу
  React.useEffect(() => {
    setOptions([]);
    if (isFirstRun.current) {
      isFirstRun.current = false;
    } else if (onChange) {
      onChange(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [depKey]);

  const loadOptions = () => {
    if (!cityId || isLoading || options.length > 0) return;

    const cached = dcCache?.get(depKey);
    if (cached) {
      setOptions(cached);
      return;
    }

    setIsLoading(true);
    getDcList({ cityId, mark })
      .then((res) => {
        const opts = (res?.data || []).map((item) => ({ value: item.id, label: item.name }));
        dcCache?.set(depKey, opts);
        setOptions(opts);
      })
      .finally(() => setIsLoading(false));
  };

  return (
    <Select
      title={label || 'Дилерский центр'}
      placeholder={cityId ? placeholder || 'Выберите из списка' : 'Сначала выберите город'}
      options={options}
      value={value}
      isLoading={isLoading}
      isSearchable
      disabled={!cityId}
      error={error}
      required={required}
      onChange={onChange}
      onFocus={loadOptions}
    />
  );
};

// Форма одной "проблемной" строки стока — те же справочные селекты, что и в форме
// редактирования авто (pages/Car/CarMainData.jsx). Марку/город матчим синхронно по
// спискам, загруженным один раз на всю таблицу (без сети). Модель/комплектацию/ДЦ
// НЕ пытаемся угадать заранее — они тесно связаны с маркой/моделью и грузятся лениво
// теми же селектами, что и везде, только когда пользователь реально их открывает —
// иначе на десятки строк сразу летит куча запросов
const PendingRowForm = ({
  row,
  allCities,
  allMarks,
  origin,
  onSubmitRow,
  dcCache,
  createdModels,
  onModelCreated,
}) => {
  const resolvedContainerIdRef = useRef(null);
  const hookFormRef = useRef(null);
  const [modelModalOpen, setModelModalOpen] = useState(false);

  // Бэк уже резолвит справочники по ходу парсинга строки и присылает id того, что
  // успело подтвердиться до момента отклонения (resolvedMarkId/resolvedCityId/
  // resolvedDcId/resolvedModelId) — это надёжнее, чем нечёткое сопоставление по
  // имени на фронте, и не требует лишних сетевых запросов. Фоллбэк на
  // сопоставление по имени остаётся только для полей, которые бэк не резолвил
  // (например строка отклонена ещё до резолвинга справочников)
  const defaults = useMemo(() => {
    const cityByName = findByName(allCities, row.cityName, 'title');
    const cityMatch = row.resolvedCityId
      ? { value: row.resolvedCityId, label: row.cityName }
      : cityByName
      ? { value: cityByName.id, label: cityByName.title }
      : null;
    const markByName = findByName(allMarks, row.markName, 'name');
    const markMatch = row.resolvedMarkId
      ? { value: row.resolvedMarkId, label: row.markName }
      : markByName
      ? { value: markByName.id, label: markByName.name }
      : null;
    const dcMatch = row.resolvedDcId ? { value: row.resolvedDcId, label: row.dcName } : null;
    const modelMatch = row.resolvedModelId
      ? { value: row.resolvedModelId, label: row.modelName }
      : null;
    // Комплектация — свободный текст (бэк матчит/создаёт её по названию сам,
    // без проверки по справочнику) — в отличие от марки/модели/города/ДЦ её
    // не нужно дожидаться резолвинга модели: то, что распарсилось из файла,
    // предзаполняем всегда, даже если модель ещё предстоит выбрать/добавить
    // вручную
    const complectMatch = row.complectationName
      ? { value: 0, label: row.complectationName }
      : null;
    const colorMatch = row.color
      ? COLORS.find((c) => c.label.toLowerCase() === row.color.toLowerCase())
      : null;
    const engineTypeMatch = row.engineType
      ? ENGINE_TYPE_OPTIONS.find((o) => o.label.toLowerCase() === row.engineType.toLowerCase())
      : null;
    // Подставляем ровно то, что пришло из файла, не сверяясь со справочником:
    // список грузится лениво по фокусу, а сверка всё равно на бэке. Заодно не
    // теряется значение, которого в справочнике нет, — оператор его увидит
    const transmissionMatch = row.transmission
      ? { value: row.transmission, label: row.transmission }
      : null;

    return {
      mark: markMatch,
      model: modelMatch,
      complect: complectMatch,
      city: cityMatch,
      dc: dcMatch,
      color: colorMatch,
      engineType: engineTypeMatch,
      transmission: transmissionMatch,
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Марка, выбранная прямо сейчас в форме — нужна модалке добавления модели,
  // чтобы привязать новую модель к тому, что пользователь реально выбрал, а не
  // только к тому, что распозналось при парсинге файла
  const [liveMark, setLiveMark] = useState(defaults.mark || null);

  const resolveContainerId = async ({ markOption, modelOption }) => {
    if (!markOption?.label || !modelOption?.label) {
      resolvedContainerIdRef.current = null;
      return;
    }
    try {
      const query = `${markOption.label} ${modelOption.label}`;
      const res = await getContainers({ markAndModel: query, page: 1 });
      resolvedContainerIdRef.current = res?.data?.containers?.[0]?.id ?? null;
    } catch (e) {
      resolvedContainerIdRef.current = null;
    }
  };

  // Модель предзаполнена бэком (редкий случай — бэк резолвит её лишь когда
  // остальное тоже сошлось) — подтягиваем containerId сразу, иначе открытие
  // комплектации не найдёт вариантов, пока пользователь не тронет марку/модель
  useEffect(() => {
    if (defaults.mark && defaults.model) {
      resolveContainerId({ markOption: defaults.mark, modelOption: defaults.model });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const inputs = [
    {
      name: 'mark',
      component: SelectMarks,
      label: 'Марка',
      placeholder: 'Выберите из списка',
      defaultValue: defaults.mark,
      rules: { required: true },
      onChangeCustom: (val) => {
        resolvedContainerIdRef.current = null;
        setLiveMark(val);
      },
    },
    {
      name: 'model',
      component: SelectModels,
      dependency: 'mark',
      dependencyAccessor: (o) => o?.value,
      label: 'Модель',
      placeholder: 'Выберите из списка',
      defaultValue: defaults.model,
      rules: { required: true },
      onChangeCustom: (modelOption, markOption) => resolveContainerId({ markOption, modelOption }),
    },
    {
      name: 'complect',
      component: SelectComplectations,
      dependency: ['mark', 'model'],
      dependencyAccessor: (arr) => arr?.map((o) => o?.value).join('|'),
      label: 'Комплектация',
      placeholder: 'Выберите или введите комплектацию',
      defaultValue: defaults.complect,
      rules: { required: true },
      params: () => resolvedContainerIdRef.current,
      // Бэк принимает произвольное название комплектации и сам заводит её,
      // если такой ещё нет — актуально прежде всего для только что созданной
      // модели (у неё физически не может быть готовых комплектаций в
      // справочнике). creatable позволяет ввести название руками, если его
      // нет среди подгруженных вариантов, вместо тупика "No options"
      creatable: true,
      isSearchable: true,
      formatCreateLabel: (input) => `Указать «${input}» как новую комплектацию`,
    },
    { name: 'vin', label: 'VIN', defaultValue: row.vin, rules: { required: true } },
    { component: SelectColor, name: 'color', label: 'Цвет кузова', defaultValue: defaults.color },
    { name: 'engineVolume', label: 'Объём двигателя', defaultValue: row.engineVolume },
    {
      component: Select,
      name: 'engineType',
      label: 'Тип двигателя',
      placeholder: 'Не важно',
      options: ENGINE_TYPE_OPTIONS,
      defaultValue: defaults.engineType,
    },
    {
      component: RowTransmissionSelect,
      name: 'transmission',
      label: 'КПП',
      placeholder: 'Не важно',
      defaultValue: defaults.transmission,
    },
    {
      name: 'price',
      label: 'РРЦ, KZT',
      defaultValue: row.priceStr,
      format: 'number',
      rules: { required: true },
    },
    { name: 'issueYear', label: 'Год выпуска', defaultValue: row.yearStr, rules: { required: true } },
    {
      component: SelectCity,
      name: 'city',
      label: 'Город',
      placeholder: 'Выберите из списка',
      defaultValue: defaults.city,
      rules: { required: true },
    },
    {
      component: RowDcSelect,
      name: 'dc',
      dependency: ['city', 'mark'],
      dependencyAccessor: (arr) => arr,
      label: 'Дилерский центр',
      defaultValue: defaults.dc,
      rules: { required: true },
      dcCache,
    },
  ];

  const [submitting, setSubmitting] = useState(false);
  const [modelCreated, setModelCreated] = useState(false);

  // Модель из файла не нашлась в справочнике — вместо того чтобы просто оставить
  // селект пустым, даём добавить её тут же и сразу подставить в форму (без
  // перезагрузки строки и без похода на отдельную страницу /cars/models)
  const handleModelCreated = (modelOption) => {
    hookFormRef.current?.setValue('model', modelOption);
    resolveContainerId({ markOption: liveMark, modelOption });
    setModelCreated(true);
    onModelCreated && onModelCreated(liveMark?.value, modelOption);
  };

  // Ту же недостающую модель могли уже добавить в другой строке с такой же
  // ошибкой (например строка 247 создала "Soul" для KIA) — подхватываем её и
  // тут, вместо того чтобы заставлять искать/добавлять по новой на каждой строке
  useEffect(() => {
    if (!liveMark?.value || !row.modelName || defaults.model || modelCreated) return;
    const key = `${liveMark.value}|${row.modelName.toLowerCase()}`;
    const match = createdModels?.[key];
    if (match) {
      hookFormRef.current?.setValue('model', match);
      resolveContainerId({ markOption: liveMark, modelOption: match });
      setModelCreated(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [createdModels, liveMark, modelCreated]);

  // Модель могла остаться нерезолвленной (resolvedModelId=null) просто потому,
  // что бэк споткнулся раньше — на городе/ДЦ/марке — и до проверки модели дело
  // вообще не дошло, а не потому что её реально нет в справочнике (см. порядок
  // резолвинга в uploadStockFromExcel: город → ДЦ → марка → модель). Показываем
  // "+ Добавить модель" только когда ошибка строки прямо называет модель
  // ненайденной, а не при любом отсутствии resolvedModelId
  const modelNotFoundError = row.errors?.some((msg) => /Модель .* не найдена/i.test(msg));
  const showAddModelAction = !defaults.model && modelNotFoundError && !modelCreated && row.modelName && liveMark;

  const handleSubmit = async (payload) => {
    // Блокируем кнопку на время запроса — иначе двойной клик/два запроса подряд
    // могут создать одно и то же авто дважды (бэк дополнительно проверяет VIN,
    // но это лишний повод для гонки, которого проще не допускать на фронте)
    setSubmitting(true);
    try {
      await onSubmitRow({
        // fileName + row = идентичность строки в сессии правки (в мультифайловой
        // пачке голый номер строки не уникален)
        fileName: row.fileName || null,
        row: row.row,
        markId: payload.mark?.value,
        modelId: payload.model?.value,
        complectationTitle: payload.complect?.label || '',
        cityId: payload.city?.value,
        dcId: payload.dc?.value,
        vin: (payload.vin || '').trim(),
        color: payload.color?.value || '',
        engineVolume: payload.engineVolume || '',
        engineType: payload.engineType?.label || null,
        transmission: payload.transmission?.value || null,
        priceStr: (payload.price || '').replace(/\s/g, ''),
        issueYear: payload.issueYear || '',
        ...decomposeOrigin(origin),
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className={styles.row}>
      {row.errors?.length > 0 && (
        <div className={styles.rowErrors}>
          {row.errors.map((msg, i) => (
            <div key={i} className={styles.rowError}>
              {msg}
            </div>
          ))}
        </div>
      )}
      {showAddModelAction && (
        <button
          type="button"
          className={styles.addModelButton}
          onClick={() => setModelModalOpen(true)}
        >
          + Добавить модель «{row.modelName}»
        </button>
      )}
      <HookForm
        ref={hookFormRef}
        inputs={inputs}
        grid={{
          display: 'grid',
          gap: '16px',
          gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))',
          alignItems: 'flex-end',
        }}
        action="Импортировать строку"
        onSubmit={handleSubmit}
        loading={submitting}
      />
      <AddModelModal
        isOpen={modelModalOpen}
        onClose={() => setModelModalOpen(false)}
        markId={liveMark?.value}
        markLabel={liveMark?.label}
        initialName={row.modelName}
        onCreated={handleModelCreated}
      />
    </div>
  );
};

export default PendingRowForm;
